import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 16
const RADIO_SIZE = 0x1000
const IMAGE_OFFSET = 0x30
const MEMORY_SIZE = RADIO_SIZE + IMAGE_OFFSET
const CHANNEL_START = 0x40
const NAME_START = 0x0a30
const CHANNEL_COUNT = 99
const NAME_CHARSET =
  '0123456789- ABCDEFGHIJKLMNOPQRSTUVWXYZ/_+*'

export type UVB5Profile = {
  protocol: 'baofeng-uvb5'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 5
  memorySize: number
  powerLevels: readonly ['Low', 'High']
  programCommand: string
  imageHeader: string
}

export const UVB5_PROFILES: readonly UVB5Profile[] = [
  {
    protocol: 'baofeng-uvb5',
    id: 'baofeng-uv-b5',
    vendor: 'Baofeng',
    model: 'UV-B5',
    summary: 'Baofeng UV-B5 dual-band handheld.',
    tags: ['Experimental', 'Read + write', '99 channels', '5-character names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: CHANNEL_COUNT,
    nameLength: 5,
    memorySize: MEMORY_SIZE,
    powerLevels: ['Low', 'High'],
    programCommand: '\u0005PROGRAM',
    imageHeader: 'KT511 Radio Program data v1.08\u0000\u0000',
  },
  {
    protocol: 'baofeng-uvb5',
    id: 'tyt-th-350',
    vendor: 'TYT',
    model: 'TH-350',
    summary: 'TYT TH-350 dual-band handheld.',
    tags: ['Experimental', 'Read + write', '128 channels', '5-character names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 128,
    nameLength: 5,
    memorySize: MEMORY_SIZE,
    powerLevels: ['Low', 'High'],
    programCommand: '\u0005TROGRAM',
    imageHeader: 'TRI350 Radio Program data v1.08\u0000',
  },
]

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

function decodeTone(value: number): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  if (value === 0 || value > 155) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  if (value > 50) {
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: DTCS_CODES[value - 51] ?? null,
      dtcsPolarity: 'N',
    }
  }
  return {
    toneMode: 'ctcss',
    ctcss: CTCSS_TONES[value - 1] ?? null,
    dtcs: null,
    dtcsPolarity: 'N',
  }
}

function encodeTone(channel: WebChannel): number {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return (
      CTCSS_TONES.indexOf(
        channel.ctcss as (typeof CTCSS_TONES)[number],
      ) + 1
    )
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    return (
      DTCS_CODES.indexOf(
        channel.dtcs as (typeof DTCS_CODES)[number],
      ) + 51
    )
  }
  return 0
}

function decodeName(image: Uint8Array, number: number): string {
  const start = NAME_START + (number - 1) * 5
  return Array.from(image.slice(start, start + 5))
    .map((value) => NAME_CHARSET[value] ?? ' ')
    .join('')
    .trim()
}

export function parseUVB5Channel(
  image: Uint8Array,
  profile: UVB5Profile,
  number: number,
): WebChannel {
  const start = CHANNEL_START + (number - 1) * BLOCK_SIZE
  const raw = image.slice(start, start + BLOCK_SIZE)
  if (raw.byteLength !== BLOCK_SIZE || raw[0] === 0xff) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const offset = decodeLbcd(raw.slice(4, 8)) ?? 0
  const duplex = raw[11] & 0x03
  const txFrequency =
    duplex === 1
      ? rxFrequency - offset
      : duplex === 2
        ? rxFrequency + offset
        : rxFrequency
  const rxTone = decodeTone(raw[9])
  const txTone = decodeTone(raw[10])
  const selected = txTone.toneMode === 'none' ? rxTone : txTone
  const polarity =
    (raw[8] & 0x10) !== 0 ? 'R' : 'N'
  return {
    number,
    empty: false,
    name: decodeName(image, number),
    rxFrequency,
    txFrequency,
    ...selected,
    dtcsPolarity: selected.toneMode === 'dtcs' ? polarity : 'N',
    mode: raw[11] & 0x20 ? 'NFM' : 'FM',
    power: raw[11] & 0x08 ? 'High' : 'Low',
    skip: (raw[11] & 0x40) !== 0,
  }
}

export function parseUVB5Channels(
  image: Uint8Array,
  profile: UVB5Profile,
): WebChannel[] {
  return Array.from({ length: profile.channelMaximum }, (_, index) =>
    parseUVB5Channel(image, profile, index + 1),
  )
}

export function applyUVB5Channel(
  image: Uint8Array,
  profile: UVB5Profile,
  channel: WebChannel,
): void {
  const start = CHANNEL_START + (channel.number - 1) * BLOCK_SIZE
  const raw = image.subarray(start, start + BLOCK_SIZE)
  const nameStart = NAME_START + (channel.number - 1) * 5
  if (channel.empty) {
    raw.fill(0xff)
    image.fill(0xff, nameStart, nameStart + 5)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  const tx = channel.txFrequency ?? channel.rxFrequency
  const split = Math.abs(tx - channel.rxFrequency) > 70_000_000
  encodeLbcd(
    split ? tx : Math.abs(tx - channel.rxFrequency),
    raw.subarray(4, 8),
  )
  const duplex = split ? 0 : tx < channel.rxFrequency ? 1 : tx > channel.rxFrequency ? 2 : 0
  const tone = encodeTone(channel)
  raw[9] = tone
  raw[10] = tone
  raw[8] =
    (channel.toneMode === 'dtcs' && channel.dtcsPolarity === 'R'
      ? 0x10
      : 0)
  raw[11] =
    duplex |
    (channel.mode === 'NFM' ? 0x20 : 0) |
    (channel.power === 'High' ? 0x08 : 0) |
    (channel.skip ? 0x40 : 0)
  const name = channel.name
    .toUpperCase()
    .replace(/[^0-9A-Z\- /_+*]/g, '')
    .slice(0, 5)
  image.fill(0xff, nameStart, nameStart + 5)
  for (let index = 0; index < name.length; index += 1) {
    image[nameStart + index] = NAME_CHARSET.indexOf(name[index])
  }
}

async function enterProgrammingMode(
  profile: UVB5Profile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  await transport.write(new TextEncoder().encode(profile.programCommand))
  if ((await transport.readExact(1, 1800))[0] !== ACK) {
    throw new Error('The radio refused UV-B5 programming mode.')
  }
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 1800)
  if (!new TextDecoder().decode(identity).startsWith('HKT511')) {
    throw new Error(
      `The connected radio does not identify as ${profile.vendor} ${profile.model}.`,
    )
  }
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 1500))[0] !== ACK) {
    throw new Error('The radio refused the UV-B5 clone session.')
  }
}

function frame(command: number, address: number): Uint8Array {
  return Uint8Array.of(
    command,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  )
}

export async function readUVB5RadioWithTransport(
  profile: UVB5Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(profile.memorySize)
    image.set(new TextEncoder().encode(profile.imageHeader))
    const total = RADIO_SIZE / BLOCK_SIZE
    for (
      let address = 0;
      address < RADIO_SIZE;
      address += BLOCK_SIZE
    ) {
      const command = frame(0x52, address)
      await transport.write(command)
      const response = await transport.readExact(20, 2500)
      if (
        response[0] !== 0x57 ||
        !command.slice(1).every((value, index) => response[index + 1] === value)
      ) {
        throw new Error(`Unexpected UV-B5 reply at 0x${address.toString(16)}.`)
      }
      image.set(response.slice(4), IMAGE_OFFSET + address)
      await transport.write(Uint8Array.of(ACK))
      await transport.readExact(1, 1500)
      onProgress?.({
        current: address / BLOCK_SIZE + 1,
        total,
        label: 'Reading Baofeng UV-B5 memory',
      })
    }
    return { image, reportedModel: 'UV-B5' }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}

export async function writeUVB5RadioWithTransport(
  profile: UVB5Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than UV-B5 memory.')
  }
  await enterProgrammingMode(profile, transport)
  try {
    const total = RADIO_SIZE / BLOCK_SIZE
    for (
      let address = 0;
      address < RADIO_SIZE;
      address += BLOCK_SIZE
    ) {
      const packet = new Uint8Array(4 + BLOCK_SIZE)
      packet.set(frame(0x57, address))
      packet.set(
        image.slice(
          IMAGE_OFFSET + address,
          IMAGE_OFFSET + address + BLOCK_SIZE,
        ),
        4,
      )
      await transport.write(packet)
      if ((await transport.readExact(1, 1800))[0] !== ACK) {
        throw new Error(
          `The radio rejected UV-B5 write block 0x${address.toString(16)}.`,
        )
      }
      onProgress?.({
        current: address / BLOCK_SIZE + 1,
        total,
        label: 'Writing Baofeng UV-B5 memory',
      })
    }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}
